import { c as _c } from "react/compiler-runtime";
import { segmentedTrackClassName } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { LABEL_CLASS, Panel } from '@/features/hof-dashboard/components/dashboard-ui';
import { HOF_DASHBOARD_AREA_LABELS, HOF_DASHBOARD_AREAS } from '@/features/hof-dashboard/constants';
import { formatCountdown, formatDate, translate } from '@/features/hof-dashboard/texts';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import type React from 'react';

/**
 * The placeholders take the frames of what replaces them: the same cards, gaps and container
 * queries as the overview of a Hof (overview-view.tsx), the screen most people land on. Only
 * the words inside pulse, so nothing moves when the data arrives.
 */

/** The line under the page's title that names the Hof's AVP. */
export const ResponsibleLineSkeleton: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-[22.5px] items-center"><Skeleton className="h-4 w-72 max-w-full" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * Words as a placeholder: the real text in the real type, invisible on the pulsing grey, so it
 * takes the height and wraps into the lines the loaded text will, at every width and with the
 * larger type on a desktop.
 */
const Words: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("bg-quiet animate-pulse rounded-md box-decoration-clone text-transparent", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className={t1}>{children}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/** The card of what is still open and how far each area is, in overview-view.tsx's markup. */
const StatsSkeleton: React.FC<{
  locale: Locale;
}> = t0 => {
  const $ = _c(30);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = translate("stillOpen", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <p className={LABEL_CLASS}><Words>{t1}</Words></p>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Words className="font-heading text-4xl leading-none font-extrabold">00</Words>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== locale) {
    t4 = translate("submissionMany", locale);
    $[5] = locale;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="flex items-baseline gap-2">{t3}<Words className="text-base font-medium">{t4}</Words></p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== locale) {
    t6 = translate("nextDeadline", locale);
    $[9] = locale;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== locale) {
    t7 = formatDate("2027-01-31", locale);
    $[11] = locale;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <span className="font-semibold">{t7}</span>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== locale) {
    t9 = formatCountdown(90, locale);
    $[15] = locale;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t8 || $[19] !== t9) {
    t10 = <p className="text-sm"><Words>{t6}{" "}{t8} ·{" "}{t9}</Words></p>;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t2 || $[23] !== t5) {
    t11 = <div className="space-y-1 p-5 @xl:p-6">{t2}{t5}{t10}</div>;
    $[21] = t10;
    $[22] = t2;
    $[23] = t5;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== locale) {
    t12 = HOF_DASHBOARD_AREAS.map(area => <div key={area} className="space-y-3 p-5 @xl:p-6"><span className="flex items-center gap-2 text-base font-semibold"><Skeleton className="h-4 w-4 shrink-0" /><Words>{HOF_DASHBOARD_AREA_LABELS[area][locale]}</Words></span><span className="block text-sm"><Words>{translate("progressCount", locale, {
            done: 0,
            total: 0
          })}</Words></span><Skeleton className="h-1.5 rounded-full" /></div>);
    $[25] = locale;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = <Panel className="divide-line grid divide-y p-0 @min-[640px]:auto-cols-fr @min-[640px]:grid-flow-col @min-[640px]:divide-x @min-[640px]:divide-y-0 @xl:p-0">{t11}{t12}</Panel>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
const RowSkeleton: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <li className="flex items-center gap-3 px-5 py-4 @xl:px-6"><div className="min-w-0 flex-1 space-y-2"><Skeleton className="h-5 w-3/5" /><Skeleton className="h-4 w-2/5" /></div><Skeleton className="h-11 w-32 shrink-0 rounded-lg" /></li>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/** The row of tabs, as wide as dashboard-tabs.tsx makes it with the same labels. */
const TabsSkeleton: React.FC<{
  locale: Locale;
}> = t0 => {
  const $ = _c(10);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(segmentedTrackClassName, "w-fit max-w-full animate-pulse overflow-hidden");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== locale) {
    t2 = translate("tabOverview", locale);
    $[1] = locale;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== locale || $[4] !== t2) {
    let t4;
    if ($[6] !== locale) {
      t4 = area => HOF_DASHBOARD_AREA_LABELS[area][locale];
      $[6] = locale;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    const t5 = HOF_DASHBOARD_AREAS.map(t4);
    let t6;
    if ($[8] !== locale) {
      t6 = translate("tabDocuments", locale);
      $[8] = locale;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t3 = <div className={t1}>{[t2, ...t5, t6].map(_temp)}</div>;
    $[3] = locale;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

/** One Hof's tabs and its overview, while its data loads. */
export const HofSkeleton: React.FC<{
  locale: Locale;
}> = t0 => {
  const $ = _c(17);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = <TabsSkeleton locale={locale} />;
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== locale) {
    t2 = <StatsSkeleton locale={locale} />;
    $[2] = locale;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="px-5 pt-5 pb-4 @xl:px-6 @xl:pt-6"><Skeleton className="h-7 w-48" /></div>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Panel className="p-0 @xl:p-0">{t3}<ul className="divide-line border-line divide-y border-t"><RowSkeleton /><RowSkeleton /><RowSkeleton /></ul></Panel>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="grid items-start gap-6 @min-[760px]:grid-cols-[minmax(0,1fr)_minmax(15rem,18rem)]">{t4}<Panel className="space-y-5"><Skeleton className="h-7 w-28" /><Skeleton className="h-5 w-full" /><Skeleton className="h-5 w-4/5" /><Skeleton className="h-5 w-full" /></Panel></div>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2) {
    t6 = <div className="@container min-w-0"><div className="space-y-6">{t2}{t5}</div></div>;
    $[7] = t2;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== locale) {
    t7 = translate("loading", locale);
    $[9] = locale;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <span className="sr-only">{t7}</span>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t1 || $[14] !== t6 || $[15] !== t8) {
    t9 = <div className="space-y-6" role="status">{t1}{t6}{t8}</div>;
    $[13] = t1;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};

/**
 * The whole dashboard before anything is known, which is also what the prerendered page holds.
 * `data-page-bar-pending` holds the place of the bar the dashboard sets above the page's title
 * (page-bar.tsx), so that bar does not push everything down when it arrives.
 */
export const DashboardSkeleton: React.FC<{
  locale: Locale;
}> = t0 => {
  const $ = _c(3);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ResponsibleLineSkeleton />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== locale) {
    t2 = <div className="space-y-6" data-page-bar-pending={true}>{t1}<HofSkeleton locale={locale} /></div>;
    $[1] = locale;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
function _temp(label) {
  return <span key={label} className="flex shrink-0 items-center gap-2 px-3 text-sm whitespace-nowrap text-transparent"><span className="hidden w-4 @xl/dashboard:block" />{label}</span>;
}